import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "step", "progressStep",
    "password", "passwordConfirmation",
    "nextBtn", "backBtn", "submitBtn",
    "stepLabel", "subdomain", "subdomainValue", "subdomainError"
  ]
  static values = {
    subdomainAvailabilityUrl: String,
    subdomainFormatMessage: String,
    subdomainCheckFailedMessage: String
  }

  connect() {
    this.currentStep = 0
    this.goToFirstStepWithErrors();
    this.showStep(this.currentStep);
  }

  async next() {
    if (this.currentStep === 0 && this.hasSubdomainTarget) this.enableSubdomain()
    if (!this.validateCurrentStep()) return

    if (this.currentStep === 0 && this.hasSubdomainTarget) {
      const subdomainAvailable = await this.validateSubdomainAvailability()
      if (!subdomainAvailable) return
    }

    this.currentStep++
    this.showStep(this.currentStep)
  }

  back() {
    this.currentStep--
    this.showStep(this.currentStep)
  }

  showStep(index) {
    this.stepTargets.forEach((el, i) => {
      el.classList.toggle("d-none", i !== index)
    })

    this.progressStepTargets.forEach((el, i) => {
      el.classList.toggle("registration-progress__step--active", i === index)
      el.classList.toggle("registration-progress__step--done", i < index)
    })

    this.backBtnTarget.classList.toggle("d-none", index === 0)
    this.nextBtnTarget.classList.toggle("d-none", index === this.lastStep)
    this.submitBtnTarget.classList.toggle("d-none", index !== this.lastStep)
  }

  get lastStep() {
    return this.stepTargets.length - 1
  }

  validateCurrentStep() {
    const step = this.stepTargets[this.currentStep]
    let valid = true
    let firstInvalidInput = null

    step.querySelectorAll("input:not([type=hidden])").forEach(input => {
      const inputValid = input.checkValidity()
      input.classList.toggle("is-invalid", !inputValid)
      input.closest(".form-group")?.classList.toggle("has-error", !inputValid)

      if (!inputValid) {
        firstInvalidInput ||= input
        valid = false
      }
    })

    if (this.currentStep === this.lastStep && this.hasPasswordTarget && this.hasPasswordConfirmationTarget) {
      const pw  = this.passwordTarget.value
      const pwc = this.passwordConfirmationTarget.value
      if (pw && pwc && pw !== pwc) {
        this.passwordConfirmationTarget.classList.add("is-invalid")
        firstInvalidInput ||= this.passwordConfirmationTarget
        valid = false
      }
    }

    if (!valid) {
      if (firstInvalidInput === this.subdomainTarget) {
        this.showSubdomainError(this.subdomainFormatMessageValue)
      }
      firstInvalidInput?.focus()
    }

    return valid
  }

  subdomainChanged() {
    this.subdomainTarget.value = this.subdomainTarget.value.toLowerCase()
    this.syncSubdomainValue()
    this.subdomainTarget.setCustomValidity("")
    this.clearSubdomainError()
  }

  enableSubdomain() {
    this.subdomainTarget.readOnly = false
  }

  async validateSubdomainAvailability() {
    this.nextBtnTarget.disabled = true

    try {
      const url = new URL(this.subdomainAvailabilityUrlValue, window.location.origin)
      url.searchParams.set("subdomain", this.subdomainTarget.value)
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`Subdomain validation failed with ${response.status}`)

      const result = await response.json()
      if (!result.available) {
        this.subdomainTarget.setCustomValidity(result.message || this.subdomainFormatMessageValue)
        this.showSubdomainError(result.message || this.subdomainFormatMessageValue)
        this.subdomainTarget.focus()
        return false
      }

      this.subdomainTarget.value = result.subdomain
      this.syncSubdomainValue()
      this.subdomainTarget.setCustomValidity("")
      this.clearSubdomainError()
      return true
    } catch (_error) {
      this.subdomainTarget.setCustomValidity(this.subdomainCheckFailedMessageValue)
      this.showSubdomainError(this.subdomainCheckFailedMessageValue)
      this.subdomainTarget.focus()
      return false
    } finally {
      this.nextBtnTarget.disabled = false
    }
  }

  showSubdomainError(message) {
    this.subdomainTarget.classList.add("is-invalid")
    this.subdomainTarget.closest(".form-group")?.classList.add("has-error")
    if (this.hasSubdomainErrorTarget) this.subdomainErrorTarget.textContent = message
  }

  clearSubdomainError() {
    this.subdomainTarget.classList.remove("is-invalid")
    this.subdomainTarget.closest(".form-group")?.classList.remove("has-error")
    if (this.hasSubdomainErrorTarget) this.subdomainErrorTarget.textContent = ""
  }

  syncSubdomainValue() {
    this.subdomainValueTarget.value = this.subdomainTarget.value
  }

  togglePassword() {
    const input = this.passwordTarget
    input.type = input.type === "password" ? "text" : "password"
  }

  goToFirstStepWithErrors() {
    const stepsWithErrors = this.stepTargets.findIndex(step =>
        step.querySelector(".is-invalid, .field_with_errors input, .invalid-feedback:not(:empty)") !== null
    )
    if (stepsWithErrors !== -1) this.currentStep = stepsWithErrors
  }
}
